import React, { useCallback, useEffect, useState } from "react";
import { Activity, Loader2, Inbox, User, Tag, Sparkles } from "lucide-react";
import { visitsApi } from "@/lib/api/visits";
import { useI18n } from "@/contexts/I18nContext";
import { toast } from "sonner";
import { formatApiErrorDetail } from "@/lib/api";
import TableToolbar from "@/components/shared/TableToolbar";
import TablePagination from "@/components/shared/TablePagination";

const TIER_COLORS = {
  SILVER:   "bg-zinc-100 text-zinc-700",
  GOLD:     "bg-amber-100 text-amber-700",
  PLATINUM: "bg-indigo-100 text-indigo-700",
};

function fmtDateTime(iso, lang) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(lang === "ar" ? "ar-EG" : "en-GB", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch (_) { return iso.slice(0, 16).replace("T", " "); }
}

export default function VisitsPage() {
  const { lang } = useI18n();
  const t = (en, ar) => lang === "ar" ? ar : en;

  const [items, setItems] = useState(null);
  const [total, setTotal] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [page, setPage] = useState(1);

  const load = useCallback(async () => {
    try {
      const data = await visitsApi.listProviderVisits({ limit: pageSize, offset: (page - 1) * pageSize });
      setItems(data.items);
      setTotal(data.total);
    } catch (err) {
      const msg = formatApiErrorDetail(err?.response?.data?.detail) || err.message;
      toast.error(msg || t("Failed to load visits", "فشل تحميل الزيارات"));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageSize, page]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [pageSize]);

  return (
    <div className="space-y-6 fade-up" data-testid="page-provider-visits">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">{t("Visit History", "سجل الزيارات")}</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            {items !== null
              ? t(`${total} visits recorded`, `${total} زيارة مسجلة`)
              : t("Loading…", "جارٍ التحميل…")}
          </p>
        </div>
      </header>

      <TableToolbar
        pageSize={pageSize}
        onPageSizeChange={setPageSize}
      />

      {items === null ? (
        <div className="flex items-center justify-center py-24">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </div>
      ) : items.length === 0 ? (
        <div className="surface-card p-12 flex flex-col items-center gap-3 text-center">
          <Inbox className="h-10 w-10 text-muted-foreground" />
          <p className="text-muted-foreground text-sm">
            {t("No visits recorded yet. Use the Verify page to log member visits.", "لا توجد زيارات بعد. استخدم صفحة التحقق لتسجيل الزيارات.")}
          </p>
        </div>
      ) : (
        <div className="surface-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-border">
                <tr className="text-left text-xs text-muted-foreground uppercase tracking-wide">
                  <th className="px-4 py-3 font-semibold">{t("Member", "العضو")}</th>
                  <th className="px-4 py-3 font-semibold">{t("Code", "الكود")}</th>
                  <th className="px-4 py-3 font-semibold">{t("Tier", "المستوى")}</th>
                  <th className="px-4 py-3 font-semibold">{t("Deal Redeemed", "العرض المستخدم")}</th>
                  <th className="px-4 py-3 font-semibold text-right">{t("Bill", "الفاتورة")}</th>
                  <th className="px-4 py-3 font-semibold text-right">{t("Saved", "وفر")}</th>
                  <th className="px-4 py-3 font-semibold">{t("Date & Time", "التاريخ والوقت")}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {items.map((v) => (
                  <tr key={v.id} className="hover:bg-muted/30 transition-colors">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="h-7 w-7 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                          <User className="h-3.5 w-3.5 text-primary" />
                        </div>
                        <span className="font-medium text-foreground">{v.member_name || "—"}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 font-mono text-muted-foreground">{v.member_code}</td>
                    <td className="px-4 py-3">
                      {v.member_tier && (
                        <span className={`text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full font-semibold ${TIER_COLORS[v.member_tier] || TIER_COLORS.SILVER}`}>
                          {v.member_tier}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {v.offer_id ? (
                        <div className="flex items-center gap-1.5 text-primary">
                          <Tag className="h-3.5 w-3.5 shrink-0" />
                          <span className="text-xs truncate max-w-[180px]">
                            {lang === "ar" ? v.offer_title_ar || v.offer_title_en : v.offer_title_en || v.offer_title_ar || "—"}
                          </span>
                        </div>
                      ) : (
                        <span className="text-xs text-muted-foreground italic">{t("Visit only", "زيارة فقط")}</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap font-mono text-xs text-muted-foreground">
                      {v.actual_invoice_amount != null
                        ? `${Number(v.actual_invoice_amount).toLocaleString()} ${t("EGP", "ج.م")}`
                        : "—"}
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      {v.discount_amount != null && v.discount_amount > 0 ? (
                        <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700">
                          <Sparkles className="h-3 w-3" />
                          {Number(v.discount_amount).toLocaleString()} {t("EGP", "ج.م")}
                        </span>
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">
                      {fmtDateTime(v.visited_at, lang)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <TablePagination total={total} page={page} pageSize={pageSize} onPageChange={setPage} />
        </div>
      )}
    </div>
  );
}
